import { useGeneratePhotographiesSinglePdf } from "@/api/pdf";
import { Photography } from "@/lib/types";
import { PropsWithChildren, useId } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import PrintDrawer from "../print/PrintDrawer";
import { FieldPresenceProvider } from "@/lib/print-field-presence/FieldPresenceContext";
import { useFieldPresenceContext } from "@/lib/print-field-presence/field-presence.context";
import { Checkbox } from "../ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel } from "../ui/form";
import {
  PhotographyPrintSettings,
  simpleTemplateFields,
} from "./photographyPrintSettings";
import { usePrintSettingFieldLabel } from "../artwork/usePrintSettingFieldLabel";
import i18n, { SupportedLocale } from "@/i18n";

type PhotographyPrintSingleDrawerProps = PropsWithChildren<{
  selectedPhotography: Photography;
  menuItemTrigger?: boolean;
  labeledTrigger?: boolean;
}>;

type FormValues = {
  settings: PhotographyPrintSettings;
};

const PhotographyPrintSingleDrawerInner = ({
  selectedPhotography,
  menuItemTrigger,
  labeledTrigger,
}: PhotographyPrintSingleDrawerProps) => {
  const { t } = useTranslation();
  const printSettingLabel = usePrintSettingFieldLabel();
  const { isPresent } = useFieldPresenceContext();
  const form = useForm<FormValues>({
    defaultValues: {
      settings: simpleTemplateFields.reduce(
        (acc, field) => ({
          ...acc,
          [field]: false,
        }),
        {} as PhotographyPrintSettings,
      ),
    },
  });
  const { mutateAsync: generatePhotographiesPdf, isPending } =
    useGeneratePhotographiesSinglePdf();
  const formId = useId();

  const onSubmit = async (data: FormValues) => {
    const { file, filename } = await generatePhotographiesPdf({
      lang: i18n.language as SupportedLocale,
      template: "simple",
      photographyId: selectedPhotography.id,
      settings: data.settings,
    });
    const url = URL.createObjectURL(file);
    const link = document.createElement("a");
    link.href = url;
    // Le nom vient de l'API (numero d'enregistrement ou collection + date).
    link.download = filename ?? "Photographs.pdf";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} id={formId}>
        <PrintDrawer
          formId={formId}
          submitButtonProps={{ isLoading: isPending }}
          hideText={true}
          iconSize={18}
          menuItemTrigger={menuItemTrigger}
          labeledTrigger={labeledTrigger}
        >
          <div className="flex flex-col gap-6">
            <div className="space-y-4">
              <FormLabel>{t("printOptions")}</FormLabel>
              {simpleTemplateFields
                .filter((option) => isPresent(option))
                .map((option) => (
                <FormField
                  key={option}
                  control={form.control}
                  name={`settings.${option}`}
                  render={({ field }) => (
                    <FormItem className="flex flex-row gap-2 space-y-0">
                      <FormControl>
                        <Checkbox
                          checked={field.value as boolean}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel>{printSettingLabel(option)}</FormLabel>
                    </FormItem>
                  )}
                />
              ))}
            </div>
          </div>
        </PrintDrawer>
      </form>
    </Form>
  );
};

const PhotographyPrintSingleDrawer = (
  props: PhotographyPrintSingleDrawerProps,
) => (
  <FieldPresenceProvider
    table="photography"
    ids={[props.selectedPhotography.id]}
  >
    <PhotographyPrintSingleDrawerInner {...props} />
  </FieldPresenceProvider>
);

export default PhotographyPrintSingleDrawer;
